/**
 * Host side of the postMessage protocol (docs/vendor-apps-contract.md §2).
 * One bridge per mounted vendor iframe. Trusts a message only when it comes
 * from the exact contentWindow we created AND the app's registered origin.
 *
 * Wire:
 *   host → app   comprehend:patient  { patient, reason, contextVersion }   reason: ready | changed | refresh
 *                comprehend:answers  { id, patientId, answers, contextVersion, answeredAt, partial, errors }
 *                comprehend:error    { id?, code }                          codes only — never names
 *   app → host   comprehend:ack          { version }   automatic, on handshake — diagnostics only
 *                comprehend:context      { patientId, markdown, yourId, name }
 *                comprehend:clearContext { patientId }
 *                comprehend:providerContext      { markdown }   about the clinician, not a patient
 *                comprehend:clearProviderContext {}
 *                comprehend:subscribe    { id, structure }
 *                comprehend:unsubscribe  { id }
 *                comprehend:height       { height }   content height; the host sizes the frame to it
 *                comprehend:status       { message, kind }   packaged adapters only (via their wrapper):
 *                                        what the app needs from the clinician, shown in the host's status line
 *                comprehend:zoom         { zoom, min, max, step }   comprehend.js zooms every app; the host shows − / + (zoom: null = opted out)
 *   host → app   comprehend:zoom         { zoom }   the clinician changed it
 */
import { MAX_SUBSCRIPTIONS, isTpiApp } from './store';

const PREFIX = 'comprehend:';

// What a vendor is told when we drop their content. The clinician gets the
// details (which two names disagreed) as a toast in Comprehend; the vendor
// only learns that nothing landed.
const VENDOR_CODE = {
  NOT_OPEN: 'STALE_PATIENT',
  NAME_MISMATCH: 'CONTEXT_REJECTED',
  NO_ENCOUNTER: 'STALE_PATIENT',
};

export function createBridge({ app, iframe, store, onSubscribe, onEvaluate, onDataRequest, windowRef = window }) {
  let destroyed = false;
  let visible = false;
  let dataInflight = 0;

  const send = (msg) => {
    if (destroyed || !iframe.contentWindow) return;
    iframe.contentWindow.postMessage(msg, app.embedOrigin);
  };

  const sendError = (id, code) => send({ type: `${PREFIX}error`, id: id ?? undefined, code });

  const api = {
    /** reason: 'ready' (frame just loaded) | 'changed' (chart switched) | 'refresh' (clinician action — send us your latest) */
    sendPatient(reason = 'changed') {
      send({ type: `${PREFIX}patient`, patient: store.patientFor(app.id), reason, contextVersion: store.contextVersion });
    },
    sendAnswers(subId, { patientId, answers, contextVersion, answeredAt, partial = false, errors = [] }) {
      send({ type: `${PREFIX}answers`, id: subId, patientId, answers, contextVersion, answeredAt, partial, errors });
    },
    sendError,
    sendZoom(zoom) {
      send({ type: `${PREFIX}zoom`, zoom });
    },
    sendVisibility(value) {
      if (!isTpiApp(app) || visible === value) return;
      visible = value;
      send({ type: `${PREFIX}visibility`, visible });
    },
    destroy() {
      destroyed = true;
      windowRef.removeEventListener('message', onMessage);
    },
  };

  function onMessage(event) {
    if (destroyed) return;
    if (event.source !== iframe.contentWindow) return;
    if (event.origin !== app.embedOrigin) return;
    const msg = event.data;
    if (!msg || typeof msg.type !== 'string' || !msg.type.startsWith(PREFIX)) return;

    switch (msg.type) {
      case `${PREFIX}data`: {
        if (typeof msg.requestId !== 'string' || msg.requestId.length > 80) break;
        const reply = (result) => {
          const safe = result.error
            ? { error: /^APP_DATA_[A-Z_]+$/.test(result.error) ? result.error : 'APP_DATA_UNAVAILABLE' }
            : { value: result.value, revision: result.revision, user: result.user && { role: result.user.role, canWriteSharedData: result.user.canWriteSharedData === true } };
          send({ type: `${PREFIX}dataResult`, requestId: msg.requestId, ...safe });
        };
        if (!['get', 'save'].includes(msg.operation)) {
          reply({ error: 'APP_DATA_BAD_REQUEST' });
          break;
        }
        if (!onDataRequest || app.enabled === false || dataInflight >= 4) {
          reply({ error: 'APP_DATA_UNAVAILABLE' });
          break;
        }
        // Identity is captured by the host, not copied from the iframe message.
        // Bound the transport even for a page that bypasses the public SDK.
        try {
          if (msg.operation === 'save' && (typeof msg.value === 'undefined' || new TextEncoder().encode(JSON.stringify(msg.value)).length > 1024 * 1024)) {
            reply({ error: 'APP_DATA_TOO_LARGE' });
            break;
          }
        } catch (e) {
          reply({ error: 'APP_DATA_INVALID_JSON' });
          break;
        }
        dataInflight++;
        Promise.resolve().then(() => onDataRequest(msg.operation, msg.value, msg.expectedRevision))
          .then((result) => reply(result || { error: 'APP_DATA_UNAVAILABLE' }))
          .catch((error) => reply({ error: error?.serverMessage || 'APP_DATA_UNAVAILABLE' }))
          .finally(() => { dataInflight--; });
        break;
      }
      case `${PREFIX}height`: {
        // The frame is sized to the app's content so the page is the only thing that scrolls.
        const h = Number(msg.height);
        if (Number.isFinite(h) && h > 0) store._setStatus(app.id, { contentHeight: Math.min(Math.max(Math.round(h), 240), 8000) });
        break;
      }
      case `${PREFIX}status`: {
        const text = String(msg.message || '').slice(0, 300);
        const kind = ['info', 'action', 'error'].includes(msg.kind) ? msg.kind : 'info';
        store._setStatus(app.id, { appStatus: text ? { text, kind } : null });
        break;
      }
      case `${PREFIX}zoom`: {
        // null: the app opted out of zoom; hide the controls.
        if (msg.zoom == null) {
          store._setStatus(app.id, { zoom: null });
          break;
        }
        const [zoom, min, max] = [Number(msg.zoom), Number(msg.min), Number(msg.max)];
        const step = Number(msg.step) > 0 ? Number(msg.step) : 0.1;
        if ([zoom, min, max].every(Number.isFinite) && min > 0 && max >= min && zoom >= min && zoom <= max) {
          store._setStatus(app.id, { zoom: { value: zoom, min, max, step } });
        }
        break;
      }
      case `${PREFIX}hello`:
        // comprehend.js just initialised inside the frame — whenever that was
        // relative to the iframe's load event. This is the one reliable moment
        // to deliver the first patient event.
        api.sendPatient('ready');
        if (isTpiApp(app)) send({type: `${PREFIX}visibility`, visible});
        store._setStatus(app.id, { helloAt: Date.now() });
        break;
      case `${PREFIX}evaluate`: {
        // Packaged TPI ribbon: evaluate exactly the subscription it displayed.
        // No navigation, note generation, or other app is triggered here.
        if (!isTpiApp(app) || !visible || !store.patient || msg.patientId !== store.patient.id ||
            store.getRef(app.id, store.patient.id) == null || !msg.structure) return;
        const entry = Array.from(store.subsFor(app.id)).find(([, sub]) => JSON.stringify(sub.structure) === JSON.stringify(msg.structure));
        if (entry && onEvaluate) onEvaluate(entry[0]);
        break;
      }
      case `${PREFIX}context`: {
        const result = store.setContext(app.id, { markdown: msg.markdown, patientId: msg.patientId, name: msg.name, yourId: msg.yourId, dob: msg.dob ?? null });
        if (!result.ok && !result.silent) sendError(undefined, VENDOR_CODE[result.reason || result.code] || result.code);
        break;
      }
      case `${PREFIX}clearContext`:
        store.clearContext(app.id, msg.patientId);
        break;
      case `${PREFIX}providerContext`: {
        const result = store.setProviderContext(app.id, { markdown: msg.markdown });
        if (!result.ok) sendError(undefined, result.code);
        break;
      }
      case `${PREFIX}clearProviderContext`:
        store.clearProviderContext(app.id);
        break;
      case `${PREFIX}subscribe`: {
        if (!msg.id) return;
        const result = store.subscribe(app.id, msg.id, msg.structure);
        if (!result.ok) {
          sendError(msg.id, result.code);
          return;
        }
        // Evaluate on subscribe if a chart is open — "subscribe once, read the first push".
        if (onSubscribe) onSubscribe(msg.id);
        break;
      }
      case `${PREFIX}unsubscribe`:
        if (msg.id) store.unsubscribe(app.id, msg.id);
        break;
      case `${PREFIX}ack`:
        // Script loaded + origin matched. Diagnostics only.
        store._setStatus(app.id, { ackedAt: new Date().toISOString(), scriptVersion: msg.version });
        break;
      default:
        break;
    }
  }

  windowRef.addEventListener('message', onMessage);
  return api;
}

export { MAX_SUBSCRIPTIONS };
